import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Check, Sparkles, Briefcase, Wrench, Building2, Package, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SubscribeModal } from "../components/SubscribeModal";
import { translateDefaultCopy, useI18n } from "../lib/i18n";
import PricingComingSoon from "./PricingComingSoon";

/**
 * v3.9.41 (Blueprint Stage 1 / C2) — public Pricing page. Renders the live plan
 * catalog grouped by audience, in the existing KontrataPH design system (navy/red,
 * card/btn-* components). Workers-free messaging is front and center.
 */
import { formatPhp } from "../lib/money";
const peso = formatPhp;

const GROUPS: { audience: string; title: string; sub: string; Icon: any }[] = [
  { audience: "worker", title: "For Workers & Professionals", sub: "Always free to find work. Upgrade only if you want more visibility.", Icon: Wrench },
  { audience: "employer", title: "For Employers & Companies", sub: "Post jobs, manage applicants and hire with the tools your team needs.", Icon: Briefcase },
  { audience: "supplier", title: "For Suppliers & Sellers", sub: "Sell materials and equipment to projects across the platform.", Icon: Package },
];

// [#9] Group one-time add-ons into a few clear packs instead of one long list, so
// the page reads as a short menu. Anything unmapped falls into "More add-ons".
const ADDON_GROUPS: { id: string; title: string; sub: string; keys: string[] }[] = [
  { id: "boost", title: "Boost a listing", sub: "Push a listing higher for a few days. Pro plans include monthly boosts.", keys: ["boost_3", "boost_7", "boost_30"] },
  { id: "feature", title: "Get featured", sub: "Priority placement and a featured badge across the marketplace.", keys: ["post_featured", "featured_project", "featured_business"] },
  { id: "services", title: "Documents & done-for-you", sub: "Contract templates and hands-on hiring help, only when you need them.", keys: ["contract_standard", "contract_specialized", "managed_shortlist", "post_standard"] },
];

function PlanCard({ p, onChoose }: { p: any; onChoose: (p: any) => void }) {
  const { lang, t } = useI18n();
  const free = !p.price;
  const planCopy = (field: string, value: string) => translateDefaultCopy(lang, `pricing.plan.${p.key}.${field}`, value);
  return (
    <div className={`card p-6 flex flex-col ${p.popular ? "border-brand-red ring-2 ring-brand-red/20" : ""}`}>
      {p.popular && <span className="chip bg-brand-red text-white self-start mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" />{t("pricing.mostPopular")}</span>}
      <h3 className="text-lg font-extrabold text-navy">{planCopy("name", p.name)}</h3>
      {p.tagline && <p className="text-sm text-mut mt-0.5">{planCopy("tagline", p.tagline)}</p>}
      <div className="mt-3">
        <span className="text-3xl font-extrabold text-navy">{free ? t("pricing.free") : peso(p.price)}</span>
        {!free && p.interval && <span className="text-sm text-mut font-semibold">/{p.interval === "year" ? t("pricing.yearShort") : t("pricing.monthShort")}</span>}
        {!free && p.priceYear ? <span className="block text-xs text-mut mt-0.5">{t("pricing.or")} {peso(p.priceYear)}/{t("pricing.year")}</span> : null}
      </div>
      <ul className="mt-4 space-y-2 flex-1">
        {(p.features || []).map((f: string, index: number) => (
          <li key={f} className="text-sm text-mut flex items-start gap-2"><Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />{planCopy(`f${index + 1}`, f)}</li>
        ))}
      </ul>
      <button onClick={() => onChoose(p)} className={`${p.popular ? "btn-red" : "btn-outline"} w-full justify-center mt-5`}>
        {free ? t("pricing.getStartedFree") : t("pricing.choosePlan")}
      </button>
    </div>
  );
}

export default function Pricing() {
  const { lang, t } = useI18n();
  const [plans, setPlans] = useState<any[] | null>(null);
  const [available, setAvailable] = useState(true);
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [chosen, setChosen] = useState<any | null>(null);
  useEffect(() => {
    api.getPlans().then((catalog) => {
      setAvailable(catalog.paidPlansAvailable === true);
      setPlans(catalog.plans);
    }).catch(() => setAvailable(false));
  }, []);

  function choose(p: any) {
    if (!user) { navigate("/login"); return; }
    if (p.kind !== "subscription") { navigate("/dashboard"); return; } // one-time add-ons purchased in context
    setChosen(p);
  }

  const oneTime = (plans || []).filter((p) => p.kind === "one_time");

  // Admin may pause between the availability probe and the catalog request.
  if (!available) return <PricingComingSoon />;

  return (
    <div>
      <section className="bg-navy text-white">
        <div className="container-x py-14 text-center">
          <span className="chip bg-white/10 text-white mb-3 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />{t("pricing.workersFree")}</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold">{t("pricing.title")}</h1>
          <p className="text-white/80 mt-3 max-w-2xl mx-auto">{t("pricing.intro")}</p>
        </div>
      </section>

      <div className="container-x py-12 space-y-14">
        {plans === null ? (
          <p className="text-center text-mut">{t("pricing.loading")}</p>
        ) : (
          <>
            {GROUPS.map((g) => {
              const items = plans.filter((p) => p.audience === g.audience && p.kind === "subscription");
              if (!items.length) return null;
              const I = g.Icon;
              return (
                <section key={g.audience}>
                  <div className="flex items-center gap-2 mb-1"><I className="w-5 h-5 text-navy" /><h2 className="text-xl font-extrabold text-navy">{t(`pricing.group.${g.audience}`, g.title)}</h2></div>
                  <p className="text-sm text-mut mb-5">{t(`pricing.group.${g.audience}Description`, g.sub)}</p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                    {items.map((p) => <PlanCard key={p.key} p={p} onChoose={choose} />)}
                  </div>
                </section>
              );
            })}

            {oneTime.length > 0 && (() => {
              const mapped = new Set(ADDON_GROUPS.flatMap((g) => g.keys));
              const groups = ADDON_GROUPS
                .map((g) => ({ ...g, items: g.keys.map((k) => oneTime.find((p) => p.key === k)).filter(Boolean) as any[] }))
                .filter((g) => g.items.length > 0);
              const other = oneTime.filter((p) => !mapped.has(p.key));
              if (other.length) groups.push({ id: "other", title: "More add-ons", sub: "Other one-time upgrades.", keys: [], items: other });
              return (
                <section>
                  <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-navy" /><h2 className="text-xl font-extrabold text-navy">{t("pricing.optionalAddons")}</h2></div>
                  <p className="text-sm text-mut mb-5">{t("pricing.optionalAddonsDescription")}</p>
                  <div className="space-y-6">
                    {groups.map((g) => (
                      <div key={g.id}>
                        <h3 className="font-bold text-navy text-sm">{t(`pricing.addon.${g.id}.title`, g.title)}</h3>
                        <p className="text-xs text-mut mb-3">{t(`pricing.addon.${g.id}.description`, g.sub)}</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {g.items.map((p) => (
                            <div key={p.key} className="card p-4 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-semibold text-navy">{translateDefaultCopy(lang, `pricing.plan.${p.key}.name`, p.name)}</div>
                                {p.tagline && <div className="text-xs text-mut truncate">{translateDefaultCopy(lang, `pricing.plan.${p.key}.tagline`, p.tagline)}</div>}
                              </div>
                              <div className="text-lg font-extrabold text-navy shrink-0">{peso(p.price)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })()}

            <section className="card p-6 bg-navy-50/50 text-center">
              <h3 className="font-bold text-navy">{t("pricing.protected.title")}</h3>
              <p className="text-sm text-mut mt-1 max-w-2xl mx-auto">{t("pricing.protected.description")}</p>
            </section>
          </>
        )}
      </div>

      <SubscribeModal open={!!chosen} onClose={() => setChosen(null)} plan={chosen} onDone={() => { setChosen(null); navigate("/billing"); }} />
    </div>
  );
}
